/**
 * Authentication Service Module
 *
 * This module manages user authentication using Amazon Cognito.
 * It provides secure authentication, session management, and token handling.
 *
 * Key Features:
 * 1. User Authentication:
 *    - Handles user login with Cognito
 *    - Manages password changes
 *    - Handles verification flows
 *
 * 2. Session Management:
 *    - Maintains secure sessions
 *    - Handles token refresh
 *    - Manages session persistence
 *
 * 3. Security Features:
 *    - Secure token storage
 *    - Cookie-based session management
 *    - Automatic session refresh
 *
 * 4. Error Handling:
 *    - Graceful handling of auth failures
 *    - User-friendly error messages
 *    - Automatic retry mechanisms
 */

// src/services/authenticate.js
import { CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import UserPool from "../components/Auth/userpool.js";
import Cookies from "js-cookie";

/**
 * User Authentication
 *
 * This function handles the core authentication process:
 * 1. Creates a new Cognito user instance
 * 2. Sets up authentication details
 * 3. Manages the authentication flow
 * 4. Handles different authentication states
 *
 * Special Cases:
 * - New password required flow
 * - Post-verification handling
 * - Error recovery mechanisms
 */
export const authenticate = async (
  Username,
  Password,
  isPostVerification = false
) => {
  const user = new CognitoUser({ Username, Pool: UserPool });
  const authDetails = new AuthenticationDetails({ Username, Password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (data) => {
        console.log("onSuccess:", data);
        const accessToken = data.getAccessToken().getJwtToken();
        Cookies.set("accessToken", accessToken, {
          secure: true,
          sameSite: "Strict",
        });
        resolve(data);
      },
      onFailure: (err) => {
        console.error("onFailure:", err);
        if (isPostVerification && err.code === "UserNotConfirmedException") {
          // If this is post-verification and the user is still not confirmed,
          // it might be a timing issue. Retry authentication after a short delay.
          setTimeout(() => {
            authenticate(Username, Password, true).then(resolve).catch(reject);
          }, 1000);
        } else {
          reject(err);
        }
      },
      newPasswordRequired: (data) => {
        console.log("newPasswordRequired:", data);
        resolve(data);
      },
    });
  });
};

/**
 * Session Refresh
 *
 * This function manages session refresh to:
 * 1. Get current user session
 * 2. Refresh expired tokens
 * 3. Update stored tokens
 * 4. Handle refresh failures
 *
 * Security Considerations:
 * - Secure token storage
 * - Automatic refresh before expiration
 * - Graceful failure handling
 */
export const refreshSession = () => {
  return new Promise((resolve, reject) => {
    const user = UserPool.getCurrentUser();
    if (user) {
      user.getSession((err, session) => {
        if (err) {
          reject(err);
        } else {
          const refreshToken = session.getRefreshToken();
          user.refreshSession(refreshToken, (err, session) => {
            if (err) {
              reject(err);
            } else {
              const accessToken = session.getAccessToken().getJwtToken();
              Cookies.set("accessToken", accessToken, {
                secure: true,
                sameSite: "Strict",
              });
              resolve(session);
            }
          });
        }
      });
    } else {
      reject(new Error("No current user"));
    }
  });
};
